import React, { useState } from 'react';
import {
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import CustomTable from '../../General/CustomTable';
import { formatShortDate, ALERT_MANAGEMENT_ROLES } from './constants';
import { AlertSchema } from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import { UserRole } from '../../../constants/user_types';

type PaginationParameters = { page: number; rowsPerPage: number };

interface AlertsTableProps {
  alerts: AlertSchema[];
  headers: any[];
  loading: boolean;
  onView: (alert: AlertSchema) => void;
  onEdit: (alert: AlertSchema) => void;
  onDeleteConfirm: (alert: AlertSchema) => void;
  extraColumns?: (alert: AlertSchema) => Record<string, React.ReactNode>;
  paginationParameters?: PaginationParameters;
  setPaginationParameters?: React.Dispatch<React.SetStateAction<PaginationParameters>>;
  totalCount?: number;
}

/**
 * Formats alert limits into a readable string
 * @param limits - Array of alert limits with name, value, and top properties
 * @returns Formatted string showing the limits
 */
const formatAlertLimits = (
  limits: Array<{ name: string; value: number; top: boolean }>,
): string => {
  if (!limits || limits.length === 0) {
    return 'Sin límites definidos';
  }

  return limits.map((limit) => {
    const operator = limit.top ? '>' : '<';
    return `${limit.name} ${operator} ${limit.value}`;
  }).join(', ');
};

const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  headers,
  loading,
  onView,
  onEdit,
  onDeleteConfirm,
  extraColumns,
  paginationParameters,
  setPaginationParameters,
  totalCount,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<AlertSchema | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const canManageAlerts = ALERT_MANAGEMENT_ROLES.includes(currentUser?.role as UserRole);

  const handleDeleteClick = (alert: AlertSchema) => {
    setAlertToDelete(alert);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!alertToDelete) return;
    setIsDeleting(true);
    await onDeleteConfirm(alertToDelete);
    setIsDeleting(false);
    setDeleteConfirmOpen(false);
    setAlertToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAlertToDelete(null);
  };

  const alertData = {
    count: totalCount || alerts.length,
    values: alerts.map((alert) => ({
      id: alert.id,
      name: alert.name,
      startDate: formatShortDate(alert.startDate),
      endDate: formatShortDate(alert.endDate),
      message: formatAlertLimits(alert.limits),
      active: alert.active && dayjs(alert.endDate).isAfter(dayjs()) ? 'Activo' : 'Inactivo',
      actions: (
        <Box display="flex" gap={1}>
          <IconButton onClick={() => onView(alert)} size="small">
            <VisibilityIcon />
          </IconButton>
          {canManageAlerts && (
            <IconButton onClick={() => onEdit(alert)} size="small">
              <EditIcon />
            </IconButton>
          )}
          {canManageAlerts && (
            <IconButton onClick={() => handleDeleteClick(alert)} size="small">
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
      ...(extraColumns ? extraColumns(alert) : {}),
    })),
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" m={3}>
          <CircularProgress size={'3rem'} />
        </Box>
      ) : (
        <CustomTable
          headers={headers}
          rows={alertData}
           paginationParameters={paginationParameters}
           setPaginationParameters={setPaginationParameters}
        />
      )}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Confirmar eliminación
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar la alerta &quot;{alertToDelete?.name}&quot;?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined" disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertsTable;
